import { type ShortUrlResponse } from '@air/api/types';

/** The wire union for `presentationSettings.colorMode` — option values on authoring surfaces mirror it verbatim. */
export type ShareLinkColorMode = NonNullable<NonNullable<ShortUrlResponse['presentationSettings']>['colorMode']>;

/*
 * The absent-value fallback for `presentationSettings.colorMode` on authoring surfaces AND the
 * public page. Links saved before the field existed have no `colorMode`; the editor shows Light
 * selected because its preview canvas renders light. The public page must force that same Light
 * (see `getShareLinkPublicColorMode`) — otherwise a logged-in visitor's workspace/OS theme leaks
 * in, so two people looking at the same link can see different themes. Re-selecting Light on such
 * a link keeps the draft clean (the editor removes the key instead of writing it) because absent
 * and explicit Light now render identically; persisting the default would only pin the link
 * against future default changes. Lives in the shared share-link-design folder (not an authoring
 * feature folder) so the public viewers don't import from an authoring feature tree.
 */
export const COLOR_MODE_DEFAULT: ShareLinkColorMode = 'light';

/*
 * The themes a link can force on the public page. `toggle` is deliberately not a member: it means
 * "let the viewer choose" (a named follow-up — share links have no designed viewer control yet),
 * so it still leaves the visitor's own theme preference in charge.
 */
export type ShareLinkForcedTheme = Extract<ShareLinkColorMode, 'light' | 'dark'>;

/*
 * The stored `colorMode` a public share page should classify for document forcing. Absent (legacy
 * links, and drafts that omit the key because Light is the default) becomes Light so viewers
 * cannot pick up workspace or OS theme. Explicit `'toggle'` and unknown values pass through
 * unchanged.
 *
 * Do not use this when the surface does not own the document (the editor Edit canvas): pass
 * `undefined` straight to `usePublicShareLinkForcedTheme` so the document stays unforced.
 *
 * The generic preserves the caller's stored-union type so owning surfaces cannot pass a bare
 * `string` into `usePublicShareLinkForcedTheme` without going through this helper first.
 */
export const getShareLinkPublicColorMode = <T extends string>(colorMode: T | undefined): T | ShareLinkColorMode =>
  colorMode ?? COLOR_MODE_DEFAULT;

/*
 * Classifies a wire `colorMode` into the theme the public page must force, or `undefined` for
 * "leave the visitor's preference alone" (`toggle`, or a value newer than the compiled union).
 * Named for its input to stay distinct from `~/utils/getShareLinkForcedTheme`, the route static
 * that narrows untyped ISR pageProps before delegating here. The parameter is deliberately
 * `string`, not `ShareLinkColorMode`: that pageProps caller narrows untyped data, and the wire can
 * outrun the compiled union anyway (the backend ships a new colorMode option before this frontend
 * deploys), so classification must be total over arbitrary strings — an unknown value must degrade
 * to "don't force", never to a wrong theme.
 *
 * Absent (`undefined`) also returns `undefined` here: that is the "this surface does not own the
 * document" signal (editor Edit canvas). Public pages must run the value through
 * `getShareLinkPublicColorMode` first so a missing key forces Light instead.
 */
export const getShareLinkForcedThemeFromColorMode = (
  colorMode: string | undefined,
): ShareLinkForcedTheme | undefined => (colorMode === 'light' || colorMode === 'dark' ? colorMode : undefined);
